import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SeoHead from "@/components/feature/SeoHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { mangaWorks } from "@/mocks/manga";
import { fitnessChapters, fitnessReaderPages } from "@/mocks/chapters";

export default function ReaderPage() {
  const { mangaId, chapterIndex } = useParams();
  const work = mangaWorks.find((w) => w.id === mangaId);
  const index = Number(chapterIndex);
  const valid =
    !!work && Number.isInteger(index) && index >= 1 && index <= work.totalChapters;

  const [panelOpen, setPanelOpen] = useState(false);

  if (!valid || !work) {
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center px-4 py-20 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-500">
          <i className="ri-emotion-sad-line"></i>
        </span>
        <h1 className="mt-5 font-heading text-xl font-black text-foreground-950">章节不存在或已下架</h1>
        <p className="mt-2 text-sm text-foreground-600">返回作品主页，从其他章节开始阅读吧</p>
        <Link
          to={`/manga/${mangaId}`}
          className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line"></i>
          返回作品主页
        </Link>
      </div>
    );
  }

  const chapter = fitnessChapters.find((c) => c.index === index);
  const pages = chapter?.pages ?? fitnessReaderPages;
  const chapterTitle = chapter?.title ?? `第${index}话`;

  const recentChapters = Array.from({ length: work.totalChapters }, (_, i) => i + 1).slice(-48);

  const jsonLd = [
    {
      "@type": "WebPage",
      name: `《${work.title}》漫画${chapterTitle}免费阅读`,
      url: `https://workoutpro0.github.io/reader/${work.id}/${index}`,
      inLanguage: "zh-CN",
      isPartOf: {
        "@type": "ComicStory",
        name: `《${work.title}》漫画`,
        url: `https://workoutpro0.github.io/manga/${work.id}`,
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://workoutpro0.github.io/" },
        {
          "@type": "ListItem",
          position: 2,
          name: `《${work.title}》漫画`,
          item: `https://workoutpro0.github.io/manga/${work.id}`,
        },
        { "@type": "ListItem", position: 3, name: chapterTitle },
      ],
    },
  ];

  return (
    <>
      <SeoHead
        title={`《${work.title}》漫画${chapterTitle}-免费在线阅读-43423漫画网`}
        description={`正在阅读《${work.title}》漫画${chapterTitle}，43423漫画网提供高清在线漫画阅读，支持上下章切换与完整章节浏览。`}
        path={`/reader/${work.id}/${index}`}
        type="article"
        image={work.cover}
        jsonLd={jsonLd}
      />

      <div className="sticky top-16 z-40 border-b border-background-200/70 bg-background-50/95 backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-4xl items-center justify-between gap-3 px-4 py-3 md:px-6">
          <Link
            to={`/manga/${work.id}`}
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-background-100 px-3 py-2 text-xs font-bold text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-600"
          >
            <i className="ri-arrow-left-line text-sm"></i>
            返回目录
          </Link>
          <div className="min-w-0 flex-1 text-center">
            <p className="truncate text-sm font-black text-foreground-950">
              《{work.title}》· {chapterTitle}
            </p>
            <p className="truncate text-[11px] text-foreground-600">
              {work.latestChapter} · {work.totalChapters}话全本
            </p>
          </div>
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setPanelOpen((v) => !v)}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 text-xs font-bold text-background-50 transition-colors hover:bg-primary-600"
              aria-expanded={panelOpen}
            >
              <i className="ri-list-unordered text-sm"></i>
              章节
            </button>
            {panelOpen && (
              <div className="absolute right-0 top-11 z-50 w-72 rounded-lg border border-background-200 bg-background-50 p-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold text-foreground-950">章节列表（最近48话）</p>
                  <button
                    type="button"
                    onClick={() => setPanelOpen(false)}
                    className="flex h-6 w-6 items-center justify-center rounded-md text-foreground-500 hover:bg-background-100"
                    aria-label="关闭章节列表"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                </div>
                <div className="mt-2 max-h-72 overflow-y-auto pr-1">
                  <div className="grid grid-cols-3 gap-1.5">
                    {recentChapters.map((num) => (
                      <Link
                        key={num}
                        to={`/reader/${work.id}/${num}`}
                        onClick={() => setPanelOpen(false)}
                        className={`flex items-center justify-center whitespace-nowrap rounded-md px-1 py-1.5 text-[11px] font-medium transition-colors ${
                          num === index
                            ? "bg-primary-500 text-background-50"
                            : "bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600"
                        }`}
                      >
                        第{num}话
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-5 md:px-6 md:py-6">
        <div className="mx-auto max-w-4xl">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: `《${work.title}》漫画`, to: `/manga/${work.id}` },
              { label: chapterTitle },
            ]}
          />

          <div className="mt-4 flex flex-col items-center gap-4">
            {pages.map((pageSrc, pageIndex) => (
              <figure
                key={pageSrc}
                className="w-full overflow-hidden rounded-lg border border-background-200 bg-background-100"
              >
                <img
                  src={pageSrc}
                  alt={`《${work.title}》漫画${chapterTitle}第${pageIndex + 1}页`}
                  title={`《${work.title}》漫画${chapterTitle}第${pageIndex + 1}页`}
                  className="mx-auto h-auto w-full max-w-3xl object-contain"
                />
                <figcaption className="border-t border-background-200 bg-background-100 py-2 text-center text-[11px] text-foreground-500">
                  第{pageIndex + 1}页 / 共{pages.length}页
                </figcaption>
              </figure>
            ))}
          </div>

          <nav className="mt-6 grid grid-cols-2 gap-3" aria-label="章节切换">
            {index > 1 ? (
              <Link
                to={`/reader/${work.id}/${index - 1}`}
                className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-100 px-4 py-3 text-sm font-bold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-arrow-left-line"></i>
                上一话
              </Link>
            ) : (
              <span className="flex items-center justify-center whitespace-nowrap rounded-md border border-background-200 bg-background-100 px-4 py-3 text-sm font-bold text-foreground-400">
                已是第一话
              </span>
            )}
            {index < work.totalChapters ? (
              <Link
                to={`/reader/${work.id}/${index + 1}`}
                className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
              >
                下一话
                <i className="ri-arrow-right-line"></i>
              </Link>
            ) : (
              <span className="flex items-center justify-center whitespace-nowrap rounded-md border border-background-200 bg-background-100 px-4 py-3 text-sm font-bold text-foreground-400">
                已是最新话
              </span>
            )}
          </nav>

          <div className="mt-6 flex flex-col items-center gap-2 rounded-lg border border-background-200/70 bg-background-100 p-5 text-center">
            <p className="text-xs font-bold text-foreground-900">
              看完这一话，别忘了看看作者的其他作品
            </p>
            <Link
              to={`/manga/${work.id}`}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-background-50 px-5 py-2.5 text-xs font-bold text-primary-600 transition-colors hover:bg-background-50/80"
            >
              <i className="ri-book-2-line"></i>
              返回《{work.title}》漫画主页
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}